<template>
  <div class="mention-editor">
    <!-- 输入区：文字与 @ 素材 chip 混排（chip 不可编辑，backspace 能整块删掉） -->
    <div
      ref="editor"
      class="me-input"
      :style="{ minHeight: minHeight }"
      contenteditable="true"
      :data-placeholder="placeholder"
      @input="onInput"
      @keydown="onKeydown"
      @paste="onPaste"
      @blur="onBlur"
    />

    <!-- 输入 @ 弹出的「引用素材」面板 -->
    <div v-if="atPanel" class="me-panel">
      <div class="me-head">可能@的內容</div>
      <div
        v-for="(s, i) in sources"
        :key="keyOf(s, i)"
        class="me-item"
        :class="{ on: atIndex === i }"
        @mousedown.prevent="pick(s, i)"
      >
        <img class="me-thumb" :src="urlOf(s)" alt="" />
        <span class="me-name">{{ nameOf(s, i) }}</span>
      </div>
      <div v-if="!sources.length" class="me-none">還沒有新增素材</div>
    </div>

    <!-- hover chip：在它上方弹一张大图 -->
    <div v-if="tip" class="me-tip" :style="{ left: tip.x + 'px', top: tip.y + 'px' }">
      <img :src="tip.src" alt="" />
    </div>

    <div v-if="maxlength" class="me-count" :class="{ over: text.length >= maxlength }">
      {{ text.length }} / {{ maxlength }}
    </div>
  </div>
</template>

<script>
import { fileUrl } from '../utils/file'

/**
 * 可 @ 引用素材的多行输入（文字与 chip 混排）。
 * v-model 绑定的是纯文本，chip 会转成 @名称；素材名与 [参考图N] 的对应由父组件处理。
 */
export default {
  name: 'MentionEditor',
  props: {
    value: { type: String, default: '' },
    // 可引用的素材：[{ path, file_path, name? }]
    sources: { type: Array, default: () => [] },
    placeholder: { type: String, default: '描述你的想法……' },
    rows: { type: Number, default: 4 },
    maxlength: { type: Number, default: 2000 }
  },
  data() {
    return {
      text: this.value || '',
      atPanel: false,
      atIndex: 0,
      tip: null
    }
  },
  computed: {
    /** 最小高度按 rows 算：一行约 27px，外加上下 padding */
    minHeight() {
      const r = Number(this.rows) || 4
      return (r * 27 + 26) + 'px'
    }
  },
  watch: {
    value(v) {
      // 外部改值（清空 / 回填）时同步到编辑器。
      //
      // 原实现只在「外部值变成空串」时才同步（v === ''），因为 contenteditable
      // 里带 chip 的 HTML 重建麻烦、改错了光标会乱跳。但「帮我写脚本」这类功能
      // 需要**回填一整段文本**，不回填就等于点了按钮没反应。
      //
      // 现在按纯文本回填：
      //   · 换行转成 <br>（plainText() 会把 <br> 读回成 \n，往返一致）
      //   · HTML 特殊字符先转义，否则文本里的 < > 会被当标签吞掉
      //   · 文本里的 @名称 会显示成普通文字而不是 chip —— 可以接受，用户接着
      //     编辑时新输入的 @ 仍会正常解析成 chip
      const next = v || ''
      // 早退：自己 emit 出去的 input 会回灌到 value，值一样就别再改 DOM，
      // 否则用户每敲一个字光标都会被重置到开头
      if (next === this.text) return
      this.text = next
      const el = this.$refs.editor
      if (!el) return
      if (next === '') {
        el.innerHTML = ''
        return
      }
      el.innerHTML = next
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>')
    }
  },
  methods: {
    fileUrl,
    keyOf(s, i) {
      return s.path || s.file_path || i
    },
    /**
     * @提及词条里的缩略图：走缩略图接口。
     * 词条里只有几十像素，原来直接拉原图（2K/4K），素材一多面板就卡。
     */
    urlOf(s) {
      return this.$thumb(s.file_path || s.path, 96)
    },
    /** 素材名：与 utils/mention.js 的 mentionName 同一实现（chip 名与替换规则必须一致） */
    nameOf(s, i) {
      return this.$mentionName(s, i)
    },
    /** 编辑器内容 → 纯文本（chip 记为 @名称） */
    plainText() {
      const el = this.$refs.editor
      if (!el) return ''
      let out = ''
      const walk = (node) => {
        for (const c of node.childNodes) {
          if (c.nodeType === 3) {
            out += c.textContent
          } else if (c.nodeType === 1) {
            if (c.dataset && c.dataset.name) {
              out += '@' + c.dataset.name
            } else if (c.tagName === 'BR') {
              out += '\n'
            } else if (c.tagName === 'DIV' || c.tagName === 'P') {
              if (out && out.charAt(out.length - 1) !== '\n') out += '\n'
              walk(c)
            } else {
              walk(c)
            }
          }
        }
      }
      walk(el)
      return out.replace(/\u00a0/g, ' ').trim()
    },
    sync() {
      this.text = this.plainText()
      this.$emit('input', this.text)
    },
    onInput() {
      if (this.maxlength && this.plainText().length > this.maxlength) {
        this.$message.warning('最多 ' + this.maxlength + ' 個字')
        return
      }
      this.sync()
      // 刚输入了 @ → 开面板（把 @ 去掉）
      const sel = window.getSelection()
      if (!sel || !sel.rangeCount) return
      const r = sel.getRangeAt(0)
      if (r.startContainer.nodeType !== 3) return
      const node = r.startContainer
      const off = r.startOffset
      if (off > 0 && node.textContent.charAt(off - 1) === '@') {
        node.textContent = node.textContent.slice(0, off - 1) + node.textContent.slice(off)
        r.setStart(node, off - 1)
        r.collapse(true)
        this.atIndex = 0
        this.atPanel = true
        this.sync()
      }
    },
    onKeydown(e) {
      if (this.atPanel) {
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          this.atIndex = (this.atIndex - 1 + this.sources.length) % Math.max(1, this.sources.length)
          return
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          this.atIndex = (this.atIndex + 1) % Math.max(1, this.sources.length)
          return
        }
        if (e.key === 'Enter' && this.sources.length) {
          e.preventDefault()
          this.pick(this.sources[this.atIndex], this.atIndex)
          return
        }
        if (e.key === 'Escape') {
          this.atPanel = false
          return
        }
      }
    },
    onPaste(e) {
      e.preventDefault()
      const t = (e.clipboardData || window.clipboardData).getData('text')
      document.execCommand('insertText', false, t)
    },
    onBlur() {
      // 延迟关闭，避免点面板时先失焦
      setTimeout(() => { this.atPanel = false }, 160)
    },
    /** 在光标处插入一个素材 chip */
    pick(s, i) {
      const el = this.$refs.editor
      if (!el || !s) {
        this.atPanel = false
        return
      }
      this.atPanel = false
      const name = this.nameOf(s, i)
      const src = this.urlOf(s)
      const chip = document.createElement('span')
      chip.className = 'me-chip'
      chip.setAttribute('contenteditable', 'false')
      chip.dataset.name = name
      chip.dataset.path = s.path || ''
      // 动态节点拿不到 scoped 的 data-v，尺寸必须 inline
      chip.style.cssText = 'display:inline-flex;align-items:center;gap:4px;vertical-align:middle;'
        + 'height:24px;padding:0 8px 0 3px;margin:0 2px;background:var(--c-primary-weak);'
        + 'border:1px solid var(--c-primary-line);border-radius:999px;font-size:12px;color:var(--c-primary-ink);user-select:none;line-height:1;'
      const img = document.createElement('img')
      img.style.cssText = 'width:18px;height:18px;border-radius:50%;object-fit:cover;display:block;flex:none;'
      img.src = src
      const nm = document.createElement('span')
      nm.style.cssText = 'max-width:150px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;'
      nm.textContent = name
      chip.appendChild(img)
      chip.appendChild(nm)
      chip.addEventListener('mouseenter', () => {
        const r = chip.getBoundingClientRect()
        this.tip = { src, x: Math.round(r.left + r.width / 2), y: Math.round(r.bottom) }
      })
      chip.addEventListener('mouseleave', () => { this.tip = null })

      const sel = window.getSelection()
      let placed = false
      if (sel && sel.rangeCount) {
        const r = sel.getRangeAt(0)
        if (el.contains(r.startContainer)) {
          r.deleteContents()
          r.insertNode(chip)
          const sp = document.createTextNode('\u00a0')
          chip.parentNode.insertBefore(sp, chip.nextSibling)
          r.setStartAfter(sp)
          r.collapse(true)
          sel.removeAllRanges()
          sel.addRange(r)
          placed = true
        }
      }
      if (!placed) {
        el.appendChild(chip)
        el.appendChild(document.createTextNode('\u00a0'))
      }
      this.sync()
      el.focus()
    }
  }
}
</script>

<style scoped>
.mention-editor {
  position: relative;
}
.me-input {
  width: 100%;
  box-sizing: border-box;
  /* 最小高度由 rows 决定（inline style），这里只约束最大高度 */
  max-height: 60vh;
  overflow-y: auto;
  padding: 12px 14px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.9;
  color: var(--c-text-1);
  background: var(--c-surface-glass);
  backdrop-filter: var(--blur-glass);
  -webkit-backdrop-filter: var(--blur-glass);
  border: 1px solid transparent;
  border-radius: var(--r-md);
  outline: none;
  word-break: break-word;
  white-space: pre-wrap;
  transition: border-color 0.16s ease;
}
.me-input:focus {
  border-color: var(--c-primary-line);
}
.me-input:empty::before {
  content: attr(data-placeholder);
  color: var(--c-text-4);
  white-space: pre-wrap;
}

/* @ 面板 */
.me-panel {
  position: absolute;
  left: 0;
  top: calc(100% + 6px);
  z-index: 20;
  width: 280px;
  max-height: 260px;
  overflow-y: auto;
  padding: 6px;
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--r-md);
  box-shadow: var(--sh-3);
}
.me-head {
  padding: 4px 8px 6px;
  font-size: 12px;
  color: var(--c-text-4);
}
.me-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: var(--r-sm);
  cursor: pointer;
}
.me-item.on,
.me-item:hover {
  background: var(--c-primary-weak);
}
.me-thumb {
  width: 24px;
  height: 24px;
  border-radius: var(--r-sm);
  object-fit: cover;
  flex: none;
}
.me-name {
  font-size: 13px;
  color: var(--c-text-1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.me-none {
  padding: 10px 8px;
  font-size: 12px;
  color: var(--c-text-4);
}

/* hover chip 的预览大图：不接鼠标，否则会闪 */
.me-tip {
  position: fixed;
  z-index: 3001;
  transform: translate(-50%, 12px);
  padding: 6px;
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--r-md);
  box-shadow: var(--sh-3);
  pointer-events: none;
  max-width: 240px;
}
.me-tip img {
  display: block;
  width: 100%;
  max-height: 200px;
  object-fit: contain;
  border-radius: var(--r-sm);
}

/* 字数 */
.me-count {
  margin-top: 6px;
  text-align: right;
  font-size: 12px;
  color: var(--c-text-4);
  font-variant-numeric: tabular-nums;
}
.me-count.over {
  color: var(--c-danger);
  font-weight: 600;
}
</style>
